import React from 'react';
import { uniqueId } from 'lodash';

import {
  findSearchesMatchingTerms,
  isValidSearchQuery,
  RecentSearch,
  SearchQueryTerm,
} from '@neptune/compound-search-domain';
import {
  ComplexSearchFilter,
  ComplexSearchFilterConverter,
  CompoundSearchCriterion,
  isEmptyFilter,
  isRegexFilter,
  normalizeCompoundOperators,
} from '@neptune/recent-searches-domain';
import { NameSearchMode } from '@neptune/shared/search-domain';

import { fromCriterionsToTerms, fromTermsToCriterions } from './compound-search-term-utils';
import { useRecentCompoundQueries } from './use-recent-search-queries';
import { useRecentSearchesMemos } from './use-recent-searches-memos';

type UseCompoundSearchHistoryReturnValue = {
  searchesMatchingQuery: RecentSearch[];
  saveRegexpSearch: (filter: SearchQueryTerm[]) => void;
};

export const useCompoundSearchHistory = ({
  namespace,
  mode,
  queryToMatch,
}: {
  namespace: string;
  mode: NameSearchMode;
  queryToMatch?: SearchQueryTerm[];
}): UseCompoundSearchHistoryReturnValue => {
  const { searches: textSearches, saveSearch: saveTextSearch } = useRecentSearchesMemos({
    namespace,
  });

  const { searches: legacyCompoundSearches, saveSearch: saveCompoundSearch } =
    useRecentCompoundQueries({
      namespace: `${namespace}-compound`,
    });

  const compoundSearches = React.useMemo(
    () => legacyCompoundSearches.map((query) => normalizeCompoundOperators(query)),
    [legacyCompoundSearches],
  );

  const textSearchesFilters: ComplexSearchFilter[] = React.useMemo(
    () => textSearches.map(ComplexSearchFilterConverter.fromChartFilter),
    [textSearches],
  );

  const compoundSearchFilters: ComplexSearchFilter[] = React.useMemo(
    () => compoundSearches.map(ComplexSearchFilterConverter.fromRegexCriterions),
    [compoundSearches],
  );

  const searches = mode === NameSearchMode.REGEX ? compoundSearchFilters : textSearchesFilters;

  const handleSaveSearch = React.useCallback(
    (filter?: ComplexSearchFilter): void => {
      if (filter && !isEmptyFilter(filter)) {
        if (isRegexFilter(filter)) {
          saveCompoundSearch(filter.value, areSearchesEqual);
        } else {
          saveTextSearch(filter.value);
        }
      }
    },
    [saveCompoundSearch, saveTextSearch],
  );

  const searchesMatchingQuery = React.useMemo(() => {
    const allRecentRegexSearches = searches.reduce<RecentSearch[]>((acc, search) => {
      if (search.searchType !== 'regex') {
        return acc;
      }

      return [...acc, { id: uniqueId(), terms: fromCriterionsToTerms(search.value) }];
    }, []);

    if (!queryToMatch) {
      return allRecentRegexSearches.filter(isValidRecentSearch);
    }

    return findSearchesMatchingTerms(queryToMatch, allRecentRegexSearches).filter(
      isValidRecentSearch,
    );
  }, [queryToMatch, searches]);

  const saveRegexpSearch = React.useCallback(
    (query: SearchQueryTerm[]) => {
      if (!isValidSearchQuery(query)) {
        return;
      }

      handleSaveSearch({
        searchType: NameSearchMode.REGEX,
        value: fromTermsToCriterions(query),
      });
    },
    [handleSaveSearch],
  );

  return {
    searchesMatchingQuery,
    saveRegexpSearch,
  };
};

function isValidRecentSearch(recentSearch: RecentSearch) {
  return isValidSearchQuery(recentSearch.terms);
}

function areSearchesEqual(a: CompoundSearchCriterion[], b: CompoundSearchCriterion[]) {
  const aText = a.map((criterion) => criterion.value).join(' ');
  const bText = b.map((criterion) => criterion.value).join(' ');
  return aText === bText;
}
